<template>
  <LoadingScreen v-if="loading" />
  <router-view v-else :key="$route.fullPath" />
  <div v-if="dataError && !loading" class="app-error-overlay">
    <span class="app-error-overlay-title">Unable to load weather data</span>
    <span class="app-error-overlay-subtitle"
      >Please check your connection.<br />The app will retry automatically.</span
    >
    <button ref="retryButton" class="app-error-overlay-button" @click="retryLoad">Retry now</button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState } from 'vuex'
import LoadingScreen from './components/core/loading-screen/LoadingScreen.vue'
import store, { clientConfig } from './store'
import router from './router'
import { applyTheme } from './utils/theme'
import { applyClientChrome } from './utils/clientChrome'
import { setLanguage } from './i18n/resolveLanguage'

let overlayKeyHandler: ((e: KeyboardEvent) => void) | null = null

export default defineComponent({
  components: {
    LoadingScreen,
  },
  computed: {
    ...mapState(['loading', 'dataError']),
  },
  methods: {
    async retryLoad() {
      store.commit('setLoading', true)
      store.commit('setDataError', false)
      try {
        await store.dispatch('fetchSdkData', router.currentRoute.value)
      } catch (error) {
        console.error('Retry failed:', error)
        store.commit('setDataError', true)
      } finally {
        store.commit('setLoading', false)
      }
    },
  },
  mounted() {
    // Multi-client (TVAPP-286): apply per-client theme + browser chrome at boot.
    const config = clientConfig.config
    applyTheme(config.theme, config.branding)
    applyClientChrome()

    // Data-error overlay (TVapp-285): listen for Enter to retry when the overlay is visible.
    overlayKeyHandler = (e: KeyboardEvent) => {
      if (!store.state.dataError) return
      e.stopImmediatePropagation()
      if (e.key === 'Enter') this.retryLoad()
    }
    window.addEventListener('keydown', overlayKeyHandler, true)
  },
  beforeUnmount() {
    if (overlayKeyHandler) window.removeEventListener('keydown', overlayKeyHandler, true)
  },
  watch: {
    dataError(newVal: boolean) {
      if (newVal) {
        this.$nextTick(() => {
          ;(this.$refs.retryButton as HTMLButtonElement | undefined)?.focus()
        })
      }
    },
    '$route.query.key': {
      handler(newLoc: string | undefined) {
        const route = router.currentRoute.value.path?.toLowerCase() ?? ''
        if (
          route.includes('location') &&
          store.state.home.location !== undefined &&
          store.state.home.location?.Key !== newLoc &&
          !newLoc?.includes('-')
        ) {
          store.dispatch('fetchSdkData', router.currentRoute.value).catch((err) => {
            console.error('Refetch on key change failed:', err)
          })
        }
      },
    },
    '$route.query.lang': {
      handler(newLang: string | undefined) {
        if (typeof newLang === 'string' && newLang.length > 0) {
          setLanguage(newLang)
        }
      },
    },
  },
})
</script>
<style lang="scss">
.app-error-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  z-index: 9999;
}

.app-error-overlay-title {
  font-size: 2rem;
}

.app-error-overlay-subtitle {
  font-size: 1.2rem;
  opacity: 0.7;
  text-align: center;
  line-height: 1.6;
}

#app {
  font-family: 'Solis';
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: white;
}

@font-face {
  font-family: 'Solis';
  src: url('./assets/fonts/Solis-Regular.woff2') format('woff2');
  font-weight: normal;
  font-style: normal;
}

.app-error-overlay-button {
  padding: 12px 24px;
  border-radius: 24px;
  border: none;
  background: rgba(255, 255, 255, 0.16);
  color: white;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 12px;
}

.app-error-overlay-button:hover,
.app-error-overlay-button:focus {
  background: rgba(255, 255, 255, 0.24);
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
}
</style>
